import { useMemo, useState, useEffect, useRef } from "react";
import cn from "classnames";
import { scrollTo } from "seamless-scroll-polyfill";

import {
  ESurveyType,
  roundAndDropTrailingZeros,
  translate,
} from "@ascendnow/shared";

import { AscSurveyResultsHeader } from "./AscSurveyResults.Header";
import { AscSurveyResultsProps } from "./AscSurveyResults.type";
import AscEmptyStateCard from "../AscEmptyStateCard/AscEmptyStateCard";
import AscAdminPagination from "../AscAdminPagination/AscAdminPagination";
import AscRadioButton from "../AscInputFields/AscRadioButton/AscRadioButton";
import styles from "./AscSurveyResults.module.scss";

function isHighestValue(key: number, values: number[]) {
  return values[key] === Math.max(...values) && values[key] > 0;
}

export function AscSurveyAnswer({
  value,
  label,
  isHighest,
  isIndividual,
}: {
  value: number;
  label: string;
  isHighest: boolean;
  isIndividual?: boolean;
}) {
  return (
    <div className={cn(styles.answer, isHighest && styles.answerHighest)}>
      <div className={styles.answerValue}>
        {isIndividual ? (
          <AscRadioButton checked={value > 0} variant="secondary" readOnly />
        ) : (
          roundAndDropTrailingZeros(value, 1) + "%"
        )}
      </div>
      <div className={styles.answerLabel}>{label}</div>
    </div>
  );
}

export default function AscSurveyResults({
  title,
  results,
  pillars,
  skills,
  modules,
  response,
  className,
  onSubmit,
  perPage = 50,
  isIndividual,
}: AscSurveyResultsProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);

  const totalPage = useMemo(
    () => Math.ceil(results?.length / perPage),
    [results, perPage]
  );

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage((prev) => prev - 1);
      if (bodyRef.current) {
        scrollTo(bodyRef.current, { behavior: "smooth", top: 0 });
      }
    }
  };

  const handleNextPage = () => {
    if (page < totalPage) {
      setPage((prev) => prev + 1);
      if (bodyRef.current) {
        scrollTo(bodyRef.current, { behavior: "smooth", top: 0 });
      }
    }
  };

  useEffect(() => {
    if (results.length) {
      setPage(1);
    }
  }, [results.length]);

  return (
    <div
      data-testid="asc-survey-results"
      className={cn(styles.ground, className)}
    >
      <AscSurveyResultsHeader
        {...{ title, pillars, skills, modules, response, onSubmit }}
      />
      <div className={styles.body} ref={bodyRef}>
        {results && Array.isArray(results) && results.length > 0 ? (
          results
            .slice((page - 1) * perPage, page * perPage)
            .map((el, index) => {
              const isAgreementType = el.type === ESurveyType.AGREEMENT;
              return (
                <div key={el.id} className={styles.question}>
                  <span className={styles.questionTitle}>
                    {(page - 1) * perPage + index + 1}. {el.question}
                  </span>
                  <div className={styles.answerList}>
                    <AscSurveyAnswer
                      value={el.answers[0]}
                      label={
                        isAgreementType
                          ? translate("stronglyDisagree")
                          : translate("never")
                      }
                      isHighest={isHighestValue(0, el.answers)}
                      isIndividual={isIndividual}
                    />
                    <AscSurveyAnswer
                      value={el.answers[1]}
                      label={
                        isAgreementType
                          ? translate("disagree")
                          : translate("rarely")
                      }
                      isHighest={isHighestValue(1, el.answers)}
                      isIndividual={isIndividual}
                    />
                    <AscSurveyAnswer
                      value={el.answers[2]}
                      label={
                        isAgreementType
                          ? translate("neutral")
                          : translate("sometimes")
                      }
                      isHighest={isHighestValue(2, el.answers)}
                      isIndividual={isIndividual}
                    />
                    <AscSurveyAnswer
                      value={el.answers[3]}
                      label={
                        isAgreementType
                          ? translate("agree")
                          : translate("often")
                      }
                      isHighest={isHighestValue(3, el.answers)}
                      isIndividual={isIndividual}
                    />
                    <AscSurveyAnswer
                      value={el.answers[4]}
                      label={
                        isAgreementType
                          ? translate("stronglyAgree")
                          : translate("always")
                      }
                      isHighest={isHighestValue(4, el.answers)}
                      isIndividual={isIndividual}
                    />
                  </div>
                </div>
              );
            })
        ) : (
          <AscEmptyStateCard />
        )}
      </div>
      <div className={styles.pagination}>
        {results && Array.isArray(results) && results.length > 0 && (
          <AscAdminPagination
            goNext={handleNextPage}
            goBack={handlePreviousPage}
            pageCount={totalPage}
            page={page}
          />
        )}
      </div>
    </div>
  );
}
